import { useCallback, useEffect, useState } from 'react';
import type { ThemeBrand } from 'src/api/types/user';

export type { ThemeBrand };

export const THEME_BRANDS: ThemeBrand[] = ['ark-flame', 'ark-classic'];
export const DEFAULT_BRAND: ThemeBrand = 'ark-flame';

export const BRAND_LABELS: Record<ThemeBrand, string> = {
  'ark-flame': 'Ark Flame',
  'ark-classic': 'Ark Classic',
};

const STORAGE_KEY = 'dms-ark:theme-brand';

const isThemeBrand = (value: unknown): value is ThemeBrand =>
  value === 'ark-flame' || value === 'ark-classic';

export const getStoredBrand = (): ThemeBrand => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isThemeBrand(stored)) return stored;
  } catch {
    // localStorage unavailable (SSR / privacy mode); fall through to default.
  }
  return DEFAULT_BRAND;
};

export const applyBrand = (brand: ThemeBrand): void => {
  document.documentElement.setAttribute('data-brand', brand);
};

export const persistBrand = (brand: ThemeBrand): void => {
  try {
    localStorage.setItem(STORAGE_KEY, brand);
  } catch {
    // Persisting is best-effort; applying the attribute still works.
  }
  applyBrand(brand);
};

export function useBrand() {
  const [brand, setBrandState] = useState<ThemeBrand>(getStoredBrand);

  const setBrand = useCallback((next: ThemeBrand) => {
    persistBrand(next);
    setBrandState(next);
  }, []);

  useEffect(() => {
    applyBrand(brand);
  }, [brand]);

  return { brand, setBrand };
}
